import React, { useEffect, useState } from 'react';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from 'antd';
import RoleProtectedField from '../../RoleProtectedField';
import { CommissionCalculator, CalculationResult } from '../../../utils/commissionCalculator';
import userService from '../../../services/user';
import { optionService } from '../../../services/option';

const { Option } = Select;

const paymentStatusOptions = [
  { value: 'paid', label: '已付款' },
  { value: 'unpaid', label: '未付款' },
];

interface FinanceTabProps {
  form: any;
  isEdit?: boolean;
  getReadOnlyProps?: (type: 'input' | 'select') => object;
}

const FinanceSection: React.FC<FinanceTabProps> = ({ form, isEdit = false, getReadOnlyProps = () => ({}) }) => {

  // ✅ useState 必须在组件函数体内部
  const [calculationResult, setCalculationResult] = useState<CalculationResult>({
    profit: 0,
    commission: 0,
    commissionRate: 0,
    hasError: false,
  });

  const [clientTypeOptions, setClientTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [sourceOptions, setSourceOptions] = useState<Array<{ value: string; label: string }>>([]);

  const calculateCommission = async () => {
    const values = form.getFieldsValue();

    try {
      const result = await CommissionCalculator.calculate({
        price: values.price,
        cost: values.cost,
        client_type: values.client_type,
      });
      setCalculationResult(result);
    } catch (error) {
      console.error('Failed to calculate commission:', error);
      setCalculationResult({
        profit: 0,
        commission: 0,
        commissionRate: 0,
        hasError: true,
        errorMessage: '计算失败',
      });
    }
  };

  // 监听 client_type/price/cost 的变化，自动重新计算
  const watchedClientType = Form.useWatch('client_type');
  const watchedPrice = Form.useWatch('price');
  const watchedCost = Form.useWatch('cost');

  useEffect(() => {
    calculateCommission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedClientType, watchedPrice, watchedCost]);

  // 销售选项（仅供官员角色显示的字段使用）
  const [sellerOptions, setSellerOptions] = useState<Array<{ value: number; label: string }>>([]);

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const res = await userService.getUsers();
        if (res.success && Array.isArray(res.data)) {
          const roleNames = ['sales_manager', 'sales'];
          const filtered = (res.data as any[])
            .filter((u) => Array.isArray(u.roles) && u.roles.some((r: any) => roleNames.includes(r.name)))
            .map((u) => ({ value: u.id as number, label: u.display_name || u.username || u.email }));
          setSellerOptions(filtered as any);
        }
      } catch (e) {
        setSellerOptions([]);
      }
    };
    loadSellers();
  }, []);

  // 加载客户等级和来源选项
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // 加载提成配置，用于在客户等级选项中展示提成比例
        const commissionRates = await CommissionCalculator.getLoadedRates();
        const commissionMap = new Map<string, number>();
        commissionRates.forEach(rate => {
          if (rate.customerType) {
            commissionMap.set(rate.customerType, rate.rate);
          }
        });

        // 加载客户等级
        const customerTypes = await optionService.get('customer_types', []);
        if (Array.isArray(customerTypes)) {
          setClientTypeOptions(customerTypes.map(type => {
            const rate = commissionMap.get(type);
            const label = rate !== undefined ? `${type}（${rate}%）` : type;
            return {
              value: type,
              label,
            };
          }));
        }

        // 加载客户来源
        const customerSources = await optionService.get('customer_sources', []);
        if (Array.isArray(customerSources)) {
          setSourceOptions(customerSources.map(source => ({
            value: source,
            label: source
          })));
        }

      } catch (error) {
        console.error('Failed to load options:', error);
        // 如果加载失败，使用默认选项
        setClientTypeOptions([
          {value: 'individual', label: '个人'},
          {value: 'company', label: '公司'},
          {value: 'organization', label: '机构'},
        ]);
        setSourceOptions([
          {value: 'website', label: '官网'},
          {value: 'referral', label: '推荐'},
          {value: 'advertisement', label: '广告'},
          {value: 'social_media', label: '社交媒体'},
        ]);
      }
    };
    loadOptions();
  }, []);

  const renderFinanceFields = (oneColumn: boolean = false) => {
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>

        {/* 客户等级 - 移动到财务信息 */}
        <Col span={12}>
          <Form.Item label="客户等级" name="client_type" rules={[{ required: true, message: '请选择客户等级' }]}>
            <Select placeholder="请选择客户等级" onChange={calculateCommission}>
              {clientTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* 客户来源 - 移动到财务信息 */}
        <Col span={12}>
          <Form.Item label="客户来源" name="source">
            <Select placeholder="请选择客户来源">
              {sourceOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* 所属销售：仅 admin 可见，保存到 orders.seller */}
        {/*<RoleProtectedField requiredRoles={['admin']}>*/}
        {/*  <Col span={24}>*/}
        {/*    <Form.Item label="所属销售" name="seller">*/}
        {/*      <Select placeholder="请选择所属销售">*/}
        {/*        {sellerOptions.map(opt => (*/}
        {/*          <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>*/}
        {/*        ))}*/}
        {/*      </Select>*/}
        {/*    </Form.Item>*/}
        {/*  </Col>*/}
        {/*</RoleProtectedField>*/}

        <Col span={12}>
          <Form.Item
            label="付款状态"
            name="payment_status"
            rules={[{ required: true, message: '请选择付款状态' }]}
          >
            <Select placeholder="请选择付款状态">
              {paymentStatusOptions.map(option => (
                <Select.Option key={option.value} value={option.label}  data-testid="order-payment-status">
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="付款日期" name="payment_at">
            <DatePicker style={{ width: '100%' }} placeholder="请选择付款日期"  data-testid="order-payment-at" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="渠道" name="channel">
            <Input placeholder="请输入渠道" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="成交价格" name="price">
            <Input placeholder="请输入成交价格" addonBefore="¥" onChange={calculateCommission} data-testid="order-price" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="成本" name="cost">
            <Input placeholder="请输入成本" addonBefore="¥" onChange={calculateCommission} data-testid="order-cost"/>
          </Form.Item>
        </Col>

        <RoleProtectedField requiredRoles={['admin']}>
          <Col span={12}>
            <Form.Item label="利润">
              <InputNumber
                value={calculationResult.hasError ? undefined : calculationResult.profit}
                placeholder={calculationResult.hasError ? '--' : undefined}
                precision={2}
                formatter={value => `¥ ${value}`}
                parser={value => Number(value?.replace(/\¥\s?|(,*)/g, '')) || 0}
                readOnly
                style={{
                  width: '100%',
                  color: calculationResult.profit >= 0 ? '#3f8600' : '#cf1322',
                }}
              />
            </Form.Item>
          </Col>
        </RoleProtectedField>

        <RoleProtectedField requiredRoles={['admin']}>
          <Col span={12}>
            <Form.Item label="本单提成">
              <InputNumber
                value={calculationResult.hasError ? undefined : calculationResult.commission}
                placeholder={calculationResult.hasError ? '--' : undefined}
                precision={2}
                formatter={value => `¥ ${value}`}
                parser={value => Number(value?.replace(/\¥\s?|(,*)/g, '')) || 0}
                readOnly
                style={{ width: '100%', color: '#722ed1' }}
              />
            </Form.Item>
          </Col>
        </RoleProtectedField>
      </Row>
    );
  };

  return <>{renderFinanceFields(true)}</>;
};

export default FinanceSection;
